import {
  PARAMETRI_PREDEFINITI,
  TARATURA_NEUTRA,
  type Canale,
  type Impianto,
  type Zona,
} from '@emacomfort/domain';

/**
 * Impianto di riferimento CSTAI, derivato da docs/reference/io-map.json.
 *
 * Le assegnazioni a confidenza `media` (A3, A6) restano marcate come tali: la
 * specifica vieta di promuoverle a fatto. Il puffer NON ha sonda (§5.8): il
 * sinottico lo dichiara invece di inventare un valore.
 */

const limiti = { min: 16, max: 28, impostatiDa: 'CSTAI — installazione' };

function sonda(id: string, assegnazione: Canale['assegnazione'], extra: Partial<Canale> = {}): Canale {
  return {
    id,
    moduloId: 'uc',
    direzione: 'INPUT',
    grandezza: 'TEMPERATURA',
    unita: '°C',
    tipoSonda: 'NTC_10K',
    taratura: { ...TARATURA_NEUTRA },
    assegnazione,
    confidenza: 'alta',
    ...extra,
  };
}

function uscita(id: string, assegnazione: Canale['assegnazione'], grandezza: Canale['grandezza'] = 'COMANDO', moduloId = 'rele'): Canale {
  return {
    id,
    moduloId,
    direzione: 'OUTPUT',
    grandezza,
    unita: grandezza === 'MODULAZIONE' ? '%' : '',
    taratura: { ...TARATURA_NEUTRA },
    assegnazione,
    confidenza: 'alta',
  };
}

function zona(id: string, nome: string, icona: Zona['icona'], ordinamento: number, canaleSonda: string, uscitaId: string, setpoint: number, tipoCarico: Zona['tipoCarico'] = 'ELETTROVALVOLA'): Zona {
  return {
    id,
    nome,
    icona,
    ordinamento,
    canaleSonda,
    canaliAttuatore: [uscitaId],
    tipoCarico,
    abilitata: true,
    setpoint,
    sovrascritta: false,
    limiti: { ...limiti },
  };
}

export function impiantoCstai(sovrascritture: Partial<Impianto> = {}): Impianto {
  const zone: Zona[] = [
    zona('z-camera1', 'Camera letto 1', 'camera', 1, 'A1', '22', 20),
    zona('z-camera2', 'Camera letto 2', 'camera', 2, 'A2', '23', 20),
    zona('z-bagni', 'Bagni', 'bagno', 3, 'A3', '24', 22, 'RELE_ON_OFF'),
    zona('z-soggiorno', 'Soggiorno', 'soggiorno', 4, 'A4', '25', 21.5),
    zona('z-cucina', 'Cucina', 'cucina', 5, 'A5', '26', 21),
    zona('z-camera3', 'Camera 3', 'cameretta', 6, 'A7', '27', 20, 'VENTILCONVETTORE'),
  ];
  zone[4]!.abilitata = false;

  const canali: Canale[] = [
    sonda('A1', { tipo: 'zona', zonaId: 'z-camera1' }),
    sonda('A2', { tipo: 'zona', zonaId: 'z-camera2' }),
    sonda('A3', { tipo: 'zona', zonaId: 'z-bagni' }, { confidenza: 'media', nota: 'io-map.A3' }),
    sonda('A4', { tipo: 'zona', zonaId: 'z-soggiorno' }),
    sonda('A5', { tipo: 'zona', zonaId: 'z-cucina' }),
    sonda('A6', { tipo: 'nessuna' }, { confidenza: 'media', nota: 'io-map.A6' }),
    sonda('A7', { tipo: 'zona', zonaId: 'z-camera3' }, { nota: 'io-map.A7' }),
    sonda('A8', { tipo: 'funzione', funzione: 'MANDATA_COLLETTORE' }),
    sonda('A9', { tipo: 'funzione', funzione: 'RITORNO_COLLETTORE' }),
    sonda('A10', { tipo: 'funzione', funzione: 'ACCUMULO_SANITARIO' }),
    sonda('A11/1', { tipo: 'funzione', funzione: 'MANDATA_GENERATORE', generatoreId: 'g-pdc' }, { moduloId: 'mux-gen' }),
    sonda('A12/1', { tipo: 'funzione', funzione: 'RITORNO_GENERATORE', generatoreId: 'g-pdc' }, { moduloId: 'mux-gen' }),
    sonda('A11/2', { tipo: 'funzione', funzione: 'MANDATA_GENERATORE', generatoreId: 'g-camino' }, { moduloId: 'mux-gen' }),
    sonda('A12/2', { tipo: 'funzione', funzione: 'RITORNO_GENERATORE', generatoreId: 'g-camino' }, { moduloId: 'mux-gen' }),
    sonda('A13', { tipo: 'funzione', funzione: 'TEMPERATURA_ESTERNA' }),
    sonda('A14', { tipo: 'funzione', funzione: 'UMIDITA_INTERNA' }, { grandezza: 'UMIDITA', unita: '%', tipoSonda: 'ANALOGICO' }),
    sonda('A15', { tipo: 'funzione', funzione: 'LUMINOSITA' }, { grandezza: 'LUMINOSITA', unita: '%', tipoSonda: 'ANALOGICO', confidenza: 'media', nota: 'io-map.A15' }),
    uscita('11', { tipo: 'funzione', funzione: 'VMC' }, 'MODULAZIONE', 'pwm'),
    uscita('12', { tipo: 'funzione', funzione: 'LOGICA_GENERATORE' }, 'MODULAZIONE', 'pwm'),
    uscita('22', { tipo: 'zona', zonaId: 'z-camera1' }),
    uscita('23', { tipo: 'zona', zonaId: 'z-camera2' }),
    uscita('24', { tipo: 'zona', zonaId: 'z-bagni' }),
    uscita('25', { tipo: 'zona', zonaId: 'z-soggiorno' }),
    uscita('26', { tipo: 'zona', zonaId: 'z-cucina' }),
    uscita('27', { tipo: 'zona', zonaId: 'z-camera3' }),
    uscita('28', { tipo: 'funzione', funzione: 'COMANDO_GENERATORE', generatoreId: 'g-camino' }),
    uscita('29', { tipo: 'funzione', funzione: 'CIRCOLATORE' }),
    uscita('30', { tipo: 'funzione', funzione: 'VALVOLA_DEVIATRICE' }),
  ];

  return {
    id: 'cstai-villa-quercia',
    tenantId: 'cstai',
    nome: 'Villa Quercia',
    indirizzo: { via: 'Via dei Platani 12', citta: 'Trani', provincia: 'BT' },
    cliente: 'Famiglia Quercia',
    dataInstallazione: '2026-10-01',
    tecnicoReferente: 'Tecnico CSTAI',
    centralina: { modello: 'Ema Comfort UC-1 (ESP32-S3)', firmware: '0.9.2', tipo: 'emacomfort', host: 'emacomfort.local' },
    moduli: [
      { id: 'uc', tipo: 'UNITA_CENTRALE', nome: 'Unità centrale', stato: 'ok' },
      { id: 'mux-gen', tipo: 'MULTIPLEX', nome: 'Multiplex generatori (A11/A12)', stato: 'ok' },
      { id: 'rele', tipo: 'RELE_230V_16A', nome: 'Modulo relè DIN 22–30', stato: 'ok' },
      { id: 'pwm', tipo: 'PWM_0_10V', nome: 'Uscite PWM 11–12', stato: 'ok' },
      { id: 'lan', tipo: 'LAN', nome: 'Ethernet W5500', stato: 'ok' },
    ],
    canali,
    zone,
    generatori: [
      { id: 'g-pdc', nome: 'Pompa di calore', tipo: 'POMPA_DI_CALORE', canaleMandata: 'A11/1', canaleRitorno: 'A12/1', canaleComando: '12', priorita: 1, modulante: true },
      { id: 'g-camino', nome: 'Termocamino', tipo: 'TERMOCAMINO', canaleMandata: 'A11/2', canaleRitorno: 'A12/2', canaleComando: '28', priorita: 2, modulante: false },
    ],
    sorgenti: [{ id: 'emacomfort', nome: 'Centralina Ema Comfort', tipo: 'centralina', comandabile: true }],
    sanitario: { presente: true, setpoint: 48, boostMinuti: 30 },
    vmc: { presente: true, modalita: 'AUTO', sogliaUmidita: 60 },
    energia: { presente: false },
    parametri: structuredClone(PARAMETRI_PREDEFINITI),
    modalita: 'RISCALDAMENTO',
    ...sovrascritture,
  };
}

/** Secondo impianto CSTAI: configurazione minima, sola unità centrale, una caldaia. */
export function impiantoCstaiMinimo(): Impianto {
  const base = impiantoCstai();
  const zone: Zona[] = [
    zona('z-soggiorno', 'Soggiorno', 'soggiorno', 1, 'A1', '22', 21),
    zona('z-camera', 'Camera', 'camera', 2, 'A2', '23', 19.5),
    zona('z-bagno', 'Bagno', 'bagno', 3, 'A3', '24', 22),
  ];
  const canali: Canale[] = [
    sonda('A1', { tipo: 'zona', zonaId: 'z-soggiorno' }),
    sonda('A2', { tipo: 'zona', zonaId: 'z-camera' }),
    sonda('A3', { tipo: 'zona', zonaId: 'z-bagno' }),
    sonda('A8', { tipo: 'funzione', funzione: 'MANDATA_COLLETTORE' }),
    sonda('A9', { tipo: 'funzione', funzione: 'RITORNO_COLLETTORE' }),
    sonda('A10', { tipo: 'funzione', funzione: 'ACCUMULO_SANITARIO' }),
    sonda('A11', { tipo: 'funzione', funzione: 'MANDATA_GENERATORE', generatoreId: 'g-caldaia' }),
    sonda('A12', { tipo: 'funzione', funzione: 'RITORNO_GENERATORE', generatoreId: 'g-caldaia' }),
    sonda('A13', { tipo: 'funzione', funzione: 'TEMPERATURA_ESTERNA' }),
    sonda('A14', { tipo: 'funzione', funzione: 'UMIDITA_INTERNA' }, { grandezza: 'UMIDITA', unita: '%', tipoSonda: 'ANALOGICO' }),
    uscita('12', { tipo: 'funzione', funzione: 'LOGICA_GENERATORE' }, 'COMANDO', 'pwm'),
    uscita('22', { tipo: 'zona', zonaId: 'z-soggiorno' }),
    uscita('23', { tipo: 'zona', zonaId: 'z-camera' }),
    uscita('24', { tipo: 'zona', zonaId: 'z-bagno' }),
    uscita('29', { tipo: 'funzione', funzione: 'CIRCOLATORE' }),
    uscita('30', { tipo: 'funzione', funzione: 'VALVOLA_DEVIATRICE' }),
  ];
  return {
    ...base,
    id: 'cstai-corso-vittorio',
    nome: 'Appartamento Corso Vittorio',
    indirizzo: { via: 'Corso Vittorio Emanuele 88', citta: 'Bari', provincia: 'BA' },
    cliente: 'Condominio Corso Vittorio — int. 4',
    dataInstallazione: '2026-11-12',
    centralina: { modello: 'Ema Comfort UC-1 (ESP32-S3)', firmware: '0.9.1', tipo: 'emacomfort', host: 'emacomfort.local' },
    moduli: base.moduli.filter((m) => m.id !== 'mux-gen'),
    canali,
    zone,
    generatori: [
      { id: 'g-caldaia', nome: 'Caldaia a gas', tipo: 'CALDAIA_GAS', canaleMandata: 'A11', canaleRitorno: 'A12', canaleComando: '12', priorita: 1, modulante: false },
    ],
    vmc: { presente: false, modalita: 'SPENTA', sogliaUmidita: 60 },
  };
}

/** Terzo impianto CSTAI: casa colonica con quattro generatori (il massimo previsto). */
export function impiantoCstaiColonica(): Impianto {
  const base = impiantoCstai();
  const generatori: Impianto['generatori'] = [
    { id: 'g-pdc', nome: 'Pompa di calore', tipo: 'POMPA_DI_CALORE', canaleMandata: 'A11/1', canaleRitorno: 'A12/1', canaleComando: '12', priorita: 1, modulante: true },
    { id: 'g-pellet', nome: 'Stufa a pellet', tipo: 'STUFA_PELLET', canaleMandata: 'A11/2', canaleRitorno: 'A12/2', canaleComando: '28', priorita: 2, modulante: false },
    { id: 'g-camino', nome: 'Termocamino', tipo: 'TERMOCAMINO', canaleMandata: 'A11/3', canaleRitorno: 'A12/3', priorita: 3, modulante: false },
    { id: 'g-gas', nome: 'Caldaia a gas', tipo: 'CALDAIA_GAS', canaleMandata: 'A11/4', canaleRitorno: 'A12/4', priorita: 4, modulante: false },
  ];
  const canaliExtra: Canale[] = [
    sonda('A11/3', { tipo: 'funzione', funzione: 'MANDATA_GENERATORE', generatoreId: 'g-camino' }, { moduloId: 'mux-gen' }),
    sonda('A12/3', { tipo: 'funzione', funzione: 'RITORNO_GENERATORE', generatoreId: 'g-camino' }, { moduloId: 'mux-gen' }),
    sonda('A11/4', { tipo: 'funzione', funzione: 'MANDATA_GENERATORE', generatoreId: 'g-gas' }, { moduloId: 'mux-gen' }),
    sonda('A12/4', { tipo: 'funzione', funzione: 'RITORNO_GENERATORE', generatoreId: 'g-gas' }, { moduloId: 'mux-gen' }),
  ];
  const canali = base.canali.map((c) =>
    c.id === '28' ? { ...c, assegnazione: { tipo: 'funzione', funzione: 'COMANDO_GENERATORE', generatoreId: 'g-pellet' } as Canale['assegnazione'] } : c,
  );
  return {
    ...base,
    id: 'cstai-casa-colonica',
    nome: 'Casa Colonica Lama',
    indirizzo: { via: 'Contrada Lama 3', citta: 'Andria', provincia: 'BT' },
    cliente: 'Azienda agricola Lama',
    dataInstallazione: '2026-12-03',
    canali: [...canali, ...canaliExtra],
    generatori,
  };
}
